import { ExcelExportHelper } from '@vzmi/component_ec_nodejs_util/dist/helpers'
import { COMMA } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import { getDefaultTimezoneMoment } from '@vzmi/component_ec_nodejs_util/dist/lib/renderUtil'
import _ from 'lodash'
import { getExcelExport } from '../../../../lib/excelUtil'
import { onlyChineseValidation } from '../../../lib/Validator'
import { lengthMax } from '../../Proposal/Common/ProposalForm/Shared/validation/validations'
import { required } from '../../../lib/FormHelper'

const getModelContentWithIndex = (index, sku, specSelectedValue, mainImages, images) => {
  return {
    [`model${index}Sku`]: sku,
    [`model${index}SpecSelectedValue`]: specSelectedValue,
    [`model${index}MainImages`]: mainImages,
    [`model${index}Images`]: images
  }
}

const MAXIMUM_MODELS = 30

export const EXPORT_BATCH_UPDATE_IMAGE_HEADERS = {
  ...{
    applicant: '提案人',
    id: '賣場編號',
    name: '賣場名稱',
    shareMediaBetweenModels: '共用同組商品圖',
    syncProductImages: '同時修改商品圖'
  },
  ..._.reduce(
    _.times(MAXIMUM_MODELS, index => index + 1),
    (modelsHeader, index) => {
      modelsHeader = {
        ...modelsHeader,
        ...getModelContentWithIndex(
          index,
          `屬性項目${index}:商品編號`,
          `屬性項目${index}:商品屬性`,
          `屬性項目${index}:商品主圖`,
          `屬性項目${index}:商品圖片`
        )
      }
      return modelsHeader
    },
    {}
  )
}

const INVERT_BATCH_UPDATE_IMAGE_HEADERS = _.invert(EXPORT_BATCH_UPDATE_IMAGE_HEADERS)

const BOOLEAN_STRING = { true: '是', false: '否' }
export const INVERT_BOOLEAN_STRING = _.invert(BOOLEAN_STRING)
const BOOLEAN_ENG = { true: 'Y', false: 'N' }
const INVERT_BOOLEAN_ENG = _.invert(BOOLEAN_ENG)

const getModelImagesByOrder = (images = []) => {
  const [noModelsFirstImage, noModelsSecondImage, ...noModelsImages] = _.flow(
    () => _.filter(images, { width: 1000, height: 1000 }),
    filteredImages => _.orderBy(filteredImages, ['order'], 'asc'),
    sortedImages => _.map(sortedImages, 'url')
  )()
  const mainImages = [noModelsFirstImage, noModelsSecondImage]
  const otherImages = noModelsImages
  return { mainImages, otherImages }
}

const getListingLayer = (models = []) => {
  const isNoModelsListing = _.isEmpty(models)
  const isTwoLayerListing = !_.isUndefined(_.get(models, '0.items[0].name'))
  if (isNoModelsListing) {
    return 0
  }

  return isTwoLayerListing ? 2 : 1
}

const getBatchUpdateImageDataFromListing = applicant => (listing = {}) => {
  const {
    models = [],
    sku = '',
    images = [],
    shareMediaBetweenModels: listingShareMediaBetweenModels
  } = listing
  const layer = getListingLayer(models)
  const isNoModelsListing = (layer === 0)
  const isTwoLayerListing = (layer === 2)
  const noModelsImages = getModelImagesByOrder(images)
  const convertedListing = {
    // 提案人預設登入供應商的使用者名稱
    applicant,
    ..._.pick(listing, ['id', 'name']),
    ...(isNoModelsListing && ({
      syncProductImages: BOOLEAN_STRING.true,
      ...getModelContentWithIndex(
        1,
        sku,
        '',
        noModelsImages.mainImages,
        noModelsImages.otherImages
      )
    })),
    ...(!isNoModelsListing && _.reduce(models, (totalModelContent, model = {}, index) => {
      const contentIndex = index + 1
      const {
        sku: oneLayerSku,
        items = [],
        images: modelImages = [],
        spec: {
          selectedValue
        } = {}
      } = model
      let sku = oneLayerSku
      let specSelectedValue = selectedValue
      // 共用同組商品圖只有單層屬性賣場才有
      let shareMediaBetweenModels = _.get(BOOLEAN_STRING, listingShareMediaBetweenModels, '')
      if (isTwoLayerListing) {
        const itemSku = _.flow(
          () => _.map(items, 'sku'),
          skuList => _.join(skuList, COMMA)
        )()
        const itemSpecSelectedValue = _.flow(
          () => _.map(items, 'spec.selectedValue'),
          selectedValueList => _.join(selectedValueList, ' / ')
        )()
        sku = itemSku
        specSelectedValue = `${selectedValue} (${itemSpecSelectedValue})`
        shareMediaBetweenModels = ''
      }
      const indexModelImages = getModelImagesByOrder(modelImages)
      const modelContent = getModelContentWithIndex(
        contentIndex,
        sku,
        specSelectedValue,
        indexModelImages.mainImages,
        indexModelImages.otherImages
      )
      totalModelContent = {
        ...totalModelContent,
        ...modelContent,
        shareMediaBetweenModels
      }
      return totalModelContent
    }, {}))
  }
  return convertedListing
}

const getBgColorCell = (cell = {}) => {
  // cell style
  // https://github.com/protobi/js-xlsx/tree/beta#cell-styles
  // color
  // https://learn.microsoft.com/en-us/dotnet/api/system.windows.media.colors?view=windowsdesktop-8.0
  return _.set({ ...cell }, 's.fill.fgColor.rgb', 'FFDADADA')
}

const HEADER_CELL_REGEXP = /[A-Z]+(?=1$)/

const getBgColorCellPrefixMap = cellObj => {
  // 根據表頭找出哪些欄位是需要加上底色
  const bgColorCellPrefixMap = _.reduce(_.keys(cellObj), (collect, field) => {
    // A1, B1, AA1, AB1 是第一列表頭
    const match = field.match(HEADER_CELL_REGEXP) || {}
    const cellValue = _.get(cellObj, `${match.input}.v`, '')
    // 賣場編號、賣場名稱、全部商品編號和商品屬性需要有底色
    const isBgColorCell = !_.isNull(`${cellValue}`.match(/賣場編號|賣場名稱|商品編號|商品屬性/))
    if (!isBgColorCell) {
      return collect
    }

    // AB1 => AB, AAA1 => AAA
    collect[field.replace('1', '')] = field
    return collect
  }, {})
  return bgColorCellPrefixMap
}

const getEmptyCellWithBgColor = (cellObj, bgColorCellPrefixMap) => {
  // 因為空資料欄位在 cellObj 不會有欄位，為了確保就算是沒有資料的欄位也有加上底色
  // 需要先計算出最大的列數再根據表頭將預期會有的欄位都先填入空資料並上底色
  // 後面實際上轉換 cellObj 有資料時會取代預設欄位，沒有資料則會沿用空的資料格
  const defaultEmptyCellWithBgColor = _.flow(
    // 找出全部列數並取得最大值
    () => _.map(_.keys(cellObj), key => +key.replace(/\D+/, '')),
    _.max,
    // 產生空的表格列並加上底色
    maxRow => _.reduce(bgColorCellPrefixMap, (collect, headerField, key) => {
      collect = {
        ...collect,
        ..._.reduce(_.times(maxRow, index => index + 1), (obj, rowIndex) => {
          obj[`${key}${rowIndex}`] = getBgColorCell({ v: '', t: 's' })
          return obj
        }, {})
      }
      return collect
    }, {})
  )()
  return defaultEmptyCellWithBgColor
}

const getConvertedCellObj = (cellObj, bgColorCellPrefixMap) => {
  // 重新轉換資料格
  const convertedCellObj = _.reduce(cellObj, (obj, cell, key) => {
    const isCell = _.isObject(cell)
    const cellPrefix = key.replace(/\d+/, '')
    const isCellWithBgColor = (
      // 從資料格前綴 AA AB AAA ...etc 判斷是否需要加上底色
      (cellPrefix in bgColorCellPrefixMap) &&
      // 第一格屬於 header 都不需要加上底色
      key !== `${cellPrefix}1`
    )
    let convertedCell = isCell
      ? _.set({ ...cell }, 's.alignment', { wrapText: true, vertical: 'top', horizontal: 'left' })
      : cell
    if (!isCell || !isCellWithBgColor) {
      obj[key] = convertedCell
      return obj
    }

    convertedCell = getBgColorCell(convertedCell)
    obj[key] = convertedCell
    return obj
  }, {})
  return convertedCellObj
}

const customBatchImageSheet = cellObj => {
  const bgColorCellPrefixMap = getBgColorCellPrefixMap(cellObj)
  const defaultEmptyCellWithBgColor = getEmptyCellWithBgColor(cellObj, bgColorCellPrefixMap)
  const covertedCellObj = getConvertedCellObj(cellObj, bgColorCellPrefixMap)
  return {
    // 預設有底色的資料格
    ...defaultEmptyCellWithBgColor,
    // 實際上 excel 資料格資料
    ...covertedCellObj,
    '!cols': [
      // applicant, id, name, shareMediaBetweenModels, syncProductImages
      ...[80, 80, 160, 100, 100],
      ..._.concat(
        // sku, specSelectedValue, mainImages, images
        ..._.times(MAXIMUM_MODELS, () => [130, 130, 200, 200])
      )
    ].map(width => ({ wpx: width }))
  }
}

const customExplainSheet = (cellObj) => {
  const covertedCellObj = _.reduce(cellObj, (obj, cell, key) => {
    if (!_.isObject(cell)) {
      _.set(obj, key, cell)
      return obj
    }

    const isHeader = !_.isNull(key.match(HEADER_CELL_REGEXP))
    if (isHeader) {
      const headerCell = _.flow(
        () => _.set({ ...cell }, 's.fill.fgColor.rgb', 'FF7030A0'),
        cellWithBg => _.set(cellWithBg, 's.font.color.rgb', 'FFFFFFFF')
      )()
      _.set(obj, key, headerCell)
      return obj
    }

    const fieldPrefix = _.replace(key, /\d+/, '')
    // noLayerListing ,oneLayerListing ,twoLayerListing
    const isCheckIcon = ['A', 'B', 'C'].includes(fieldPrefix)
    const border = {
      style: 'thin',
      color: { rgb: '#FF000000' }
    }
    const ruleCell = _.flow(
      () => _.set({ ...cell }, 's.border', { left: border, top: border, bottom: border, right: border }),
      newCell => _.set(newCell, 's.alignment', {
        wrapText: true,
        vertical: isCheckIcon ? 'center' : 'top',
        horizontal: isCheckIcon ? 'center' : 'left'
      })
    )()
    _.set(obj, key, ruleCell)

    return obj
  }, {})
  return {
    ...covertedCellObj,
    // column width
    // https://github.com/protobi/js-xlsx#worksheet-object
    // noLayerListing ,oneLayerListing ,twoLayerListing ,fieldName ,fieldDescription ,validValue
    '!cols': [80, 80, 80, 140, 200, 500].map(width => ({ wpx: width }))
  }
}

const SHEET = {
  BATCH_IMAGE: 'batchImage',
  EXPLAIN: '欄位說明'
}

const EXPLAIN_HEADERS = _.reduce(
  {
    noLayerListing: '無選項賣場',
    oneLayerListing: '單層選項賣場',
    twoLayerListing: '雙層選項賣場',
    fieldName: '欄位名稱',
    fieldDescription: '欄位說明',
    validValue: '有效數值'
  },
  (collect, text, key) => {
    collect.push({ text, key })
    return collect
  },
  []
)

const EXPLAIN_DATA = [
  {},
  {
    noLayerListing: '✓',
    oneLayerListing: '✓',
    twoLayerListing: '✓',
    fieldName: '提案人',
    fieldDescription: '此筆提案之提案人',
    validValue: '10 個中文字以下'
  },
  {
    noLayerListing: ' ',
    oneLayerListing: '✓',
    twoLayerListing: ' ',
    fieldName: '共用同組商品圖',
    fieldDescription: '是否共用同一組商品圖，僅適用於單層屬性賣場',
    validValue: '可接受以下值.\n是 或 Y => 共用同一組商品圖\n否 或 N => 不共用同一組商品圖\n\n若設定為『是』/ Y 。將以「屬性項目1」之圖片設定為共用圖片'
  },
  {
    noLayerListing: '✓',
    oneLayerListing: ' ',
    twoLayerListing: ' ',
    fieldName: '同時修改商品圖片',
    fieldDescription: '修改賣場圖片時，是否同時修改商品圖片，僅適用於無屬性賣場',
    validValue: '可接受以下值.\n是 或 Y => 修改賣場圖，同時修改商品圖\n否 或 N => 僅修改賣場圖，不修改商品圖'
  },
  {
    noLayerListing: '✓',
    oneLayerListing: '✓',
    twoLayerListing: '✓',
    fieldName: '屬性項目1~30:商品編號',
    fieldDescription: '商品編號;由系統匯出，請勿調整',
    validValue: '"顯示綁定該賣場之商品編號，用於辨識用，請勿調整此欄位\n\n例如：\n無屬性、單層屬性：111111111\n雙層屬性：111111111,222222222,333333333'
  },
  {
    noLayerListing: ' ',
    oneLayerListing: '✓',
    twoLayerListing: '✓',
    fieldName: '屬性項目1~30:商品屬性',
    fieldDescription: '商品屬性;由系統匯出，請勿調整',
    validValue: '"顯示該商品的屬性資訊，用於辨識用，請勿調整此欄位\n\n例如：\n單層屬性：粉紅色\n雙層屬性：粉紅色 (Free / XXS / XS)'
  },
  {
    noLayerListing: '✓',
    oneLayerListing: '✓',
    twoLayerListing: '✓',
    fieldName: '屬性項目1~30:商品主圖',
    fieldDescription: '兩張主圖，圖片尺寸至少 1000x1000 以上',
    validValue: '• 每張圖片檔名請以逗號","區隔開\n• 接受圖檔格式: JPG,JPEG,PNG\n• 圖片比例 1:1 之方型圖\n• 圖片尺寸至少 1000x1000 以上\n• 系統匯出為賣場圖片網址，若不修改此圖，請維持既有圖片網址進行上傳\n例如: image1.jpg,https://s.yimg.com/zp/MerchandiseImages/xxx.jpg'
  },
  {
    noLayerListing: ' ',
    oneLayerListing: ' ',
    twoLayerListing: ' ',
    fieldName: '屬性項目1~30:商品圖片',
    fieldDescription: '最多8張圖片; 圖片尺寸至少 400x400 以上之商品圖。',
    validValue: '• 最多8張商品圖片，每張圖片檔名請以逗號","區隔開\n• 接受圖檔格式: JPG,JPEG,PNG\n• 圖片比例 1:1 之方型圖\n• 圖片尺寸至少 400x400 以上\n• 系統匯出為賣場圖片網址，若不修改此圖，請維持既有圖片網址進行上傳\n例如: image1.jpg,image2.png,image3.jpeg,https://s.yimg.com/zp/MerchandiseImages/xxx1.jpg,https://s.yimg.com/zp/MerchandiseImages/xxx2.jpg'
  }
]

export const exportExcel = async (listings, applicant, excelPrefix = '') => {
  const data = listings.map(getBatchUpdateImageDataFromListing(applicant))
  const helper = new ExcelExportHelper({
    headers: EXPORT_BATCH_UPDATE_IMAGE_HEADERS,
    fileName: `${excelPrefix}購物中心批次修改圖片{{ nowDate.format("YYYYMMDDHHmmss") }}`,
    bookType: 'xlsx'
  })
  const AsyncExcelExport = await getExcelExport()
  const option = {
    fileName: helper.getFileName({
      nowDate: getDefaultTimezoneMoment()
    }),
    sheets: [
      {
        name: SHEET.BATCH_IMAGE,
        headers: helper.getSheetHeaders(),
        data: helper.convertDataByHeaders(data)
      },
      {
        name: SHEET.EXPLAIN,
        headers: EXPLAIN_HEADERS,
        data: EXPLAIN_DATA
      }
    ]
  }
  const ExcelExporter = new AsyncExcelExport(option)
  ExcelExporter.download(
    { bookType: helper.getBookType() },
    {
      [SHEET.BATCH_IMAGE]: customBatchImageSheet,
      [SHEET.EXPLAIN]: customExplainSheet
    }
  )
}

export const getUpdateImageExcelData = excelData => {
  const updateImageExcelData = _.flow(
    () => _.get(excelData, `data.${SHEET.BATCH_IMAGE}.data`, []),
    // batchImage sheet 資料中文欄位轉成同匯出時的英文欄位
    data => _.reduce(data, (collect, row) => {
      const transformRow = _.reduce(row, (newRow, cell, key) => {
        // 轉成英文欄位，對應不到維持原欄位名稱(EX: rowId)
        const transformCellKey = _.get(INVERT_BATCH_UPDATE_IMAGE_HEADERS, key, key)
        _.set(newRow, transformCellKey, `${cell}`)
        return newRow
      }, {})
      return [...collect, transformRow]
    }, [])
  )()
  return updateImageExcelData
}

export const getLocalImagesFromExcelData = excelData => {
  const localImages = _.flow(
    () => _.reduce(excelData, (images, row) => {
      const imageFields = _.keys(row).filter(field => field.match(/model(.*)Images$/))
      const newImages = _.flow(
        () => imageFields.map(imageField => _.get(row, imageField, '')),
        imageData => {
          const filterEmptyImageData = _.filter(imageData, item => !_.isEmpty(item))
          if (_.isEmpty(filterEmptyImageData)) {
            return []
          }
          return _.join(filterEmptyImageData, COMMA).split(COMMA)
        },
        imageData => _.filter(imageData, image => !image.startsWith('http'))
      )()
      return [...images, ...newImages]
    }, []),
    _.concat,
    _.uniq
  )()
  return localImages
}

const validationChain = validates => (...args) => {
  const result = _.reduce(validates, (message, validate) => {
    if (!_.isUndefined(message)) {
      return message
    }

    message = validate(...args)
    return message
  }, undefined)
  return result
}

const NOT_MODEL_FIELD = 'NOT_MODEL_FIELD'
const INVERT_BOOLEAN_MAP = {
  ...INVERT_BOOLEAN_STRING,
  ...INVERT_BOOLEAN_ENG
}
const requiredValidation = required({ msg: '不可為空' })
const BOOLEAN_ERROR_MESSAGE = `僅可接受 ${_.join(_.keys(INVERT_BOOLEAN_MAP), '、')}`
const MAIN_IMAGES_LIMIT = 2
const OTHER_IMAGES_LIMIT = 8
export const checkExcelData = (updateImageExcelData = [], listings = []) => {
  const listingMap = _.keyBy(listings, 'id')
  const result = _.reduce(updateImageExcelData, (collect, row = {}, index) => {
    const { id: listingId, shareMediaBetweenModels } = row
    const listing = _.get(listingMap, listingId, {})
    // 賣場不存在後面就不驗了
    if (_.isEmpty(listing)) {
      const errorField = { id: '賣場不存在' }
      const rowResult = { isVaild: false, field: errorField }
      collect[index] = rowResult
      return collect
    }

    const { models = [] } = listing
    const layer = getListingLayer(models)
    const isNoModelsListing = layer === 0
    const isOneModelsListing = layer === 1
    // 將資料格分群，1-30 model 和其他欄位分開驗證
    const groupedFields = _.flow(
      () => _.keys(row),
      fields => _.groupBy(fields, field => _.get(field.match(/(model\d+)/), '0', NOT_MODEL_FIELD))
    )()
    const errorField = _.reduce(groupedFields, (error, fields, group) => {
      return _.reduce(fields, (obj, field) => {
        const fieldName = _.get(EXPORT_BATCH_UPDATE_IMAGE_HEADERS, field, field)
        const fieldValue = _.get(row, field, '')
        const isEmptyFieldValue = _.isEmpty(fieldValue)
        const getErrorMessage = msg => `${fieldName} ${msg}`
        if (group === NOT_MODEL_FIELD) {
          let message
          switch (field) {
            case 'applicant': {
              message = validationChain([
                requiredValidation,
                onlyChineseValidation({ msg: '請輸入中文全名' }),
                lengthMax('APPLICANT')
              ])(fieldValue)
              break
            }
            case 'syncProductImages': {
              if (isNoModelsListing && !(fieldValue in INVERT_BOOLEAN_MAP)) {
                message = BOOLEAN_ERROR_MESSAGE
                break
              }
              break
            }
            case 'shareMediaBetweenModels': {
              if (isOneModelsListing && !(fieldValue in INVERT_BOOLEAN_MAP)) {
                message = BOOLEAN_ERROR_MESSAGE
                break
              }
              break
            }
            default:
              break
          }
          if (!_.isEmpty(message)) {
            _.set(obj, field, getErrorMessage(message))
          }
          return obj
        }

        const modelIndex = _.replace(group, /model/, '')
        const model = _.get(models, +(modelIndex) - 1, {})
        // 單雙層根據賣場 models 判斷是否有這個商品
        const isModelInListing = isNoModelsListing
          // 無屬性賣場只驗第一筆
          ? modelIndex === '1'
          // 非共用同組商品圖賣場裡找得到的商品都需要驗圖片欄位
          : !_.isEmpty(model)
        // 單層屬性賣場共用同組商品圖
        const isShareMediaBetweenModels = (
          isOneModelsListing &&
          INVERT_BOOLEAN_MAP[shareMediaBetweenModels]
        )
        const isMainImages = field.endsWith('MainImages')
        const isOtherImages = !isMainImages && field.endsWith('Images')
        if (
          !(
            isModelInListing &&
            // 要驗的欄位只有圖片
            (isMainImages || isOtherImages) &&
            // 單層屬性賣場共用同組商品圖只驗第一筆
            (isShareMediaBetweenModels ? modelIndex === '1' : true)
          )
        ) {
          return obj
        }

        const imageUrls = _.split(fieldValue, COMMA)
        const imageUrlCount = _.size(imageUrls)
        const isInvalidUrlExist = !isEmptyFieldValue && _.some(imageUrls, imageUrl => {
          const isOnlineUrl = imageUrl.startsWith('http')
          const isYimgUrl = imageUrl.startsWith('https://img.yec.tw') ||
            imageUrl.startsWith('https://s.yimg.com')
          return !isYimgUrl && isOnlineUrl
        })
        const isUrlNotInListing = !isEmptyFieldValue && !isInvalidUrlExist && _.some(imageUrls, imageUrl => {
          const isOnlineUrl = imageUrl.startsWith('http')
          if (!isOnlineUrl) {
            return false
          }
          const isImageUrlInListingImages = isNoModelsListing
            ? _.some(listing.images, imageObj => {
              return imageObj.url === imageUrl
            })
            : _.some(listing.models, modelObj => {
              return _.some(modelObj.images, imageObj => {
                return imageObj.url === imageUrl
              })
            })
          return !isImageUrlInListingImages
        })
        let message = ''
        switch (true) {
          case isInvalidUrlExist: {
            message = '不允許外站圖片'
            break
          }
          case isUrlNotInListing: {
            message = '圖片不屬於原賣場'
            break
          }
          case isMainImages: {
            if (isEmptyFieldValue) {
              message = '不可為空'
              break
            }
            if (imageUrlCount !== MAIN_IMAGES_LIMIT) {
              message = `必須為 ${MAIN_IMAGES_LIMIT} 張`
              break
            }
            break
          }
          case isOtherImages: {
            if (imageUrlCount > OTHER_IMAGES_LIMIT) {
              message = `最多 ${OTHER_IMAGES_LIMIT} 張`
              break
            }
            break
          }
          default:
            break
        }
        if (!_.isEmpty(message)) {
          _.set(obj, field, getErrorMessage(message))
        }
        return obj
      }, error)
    }, {})
    const isVaild = _.isEmpty(errorField)
    // 有驗出問題才寫入，如果最後 result 是空的代表全部驗過
    if (!isVaild) {
      const rowResult = {
        isVaild,
        errorMessages: _.values(errorField),
        field: errorField
      }
      collect[index] = rowResult
    }
    return collect
  }, {})

  return result
}
